import React, { useState } from 'react';
import { FaTimes, FaLayerGroup, FaMapMarkerAlt, FaChartArea } from 'react-icons/fa';

const MapLayerControl = ({ layers, onLayerChange, onClose }) => {
  const [activeLayers, setActiveLayers] = useState({
    basemap: 'satellite',
    sensors: true,
    fields: true,
    ndvi: false,
    evi: false,
    thermal: false,
    ndre: false
  });
  
  const [opacityValues, setOpacityValues] = useState({
    ndvi: 0.7,
    evi: 0.7,
    thermal: 0.7,
    ndre: 0.7
  });
  
  const handleBaseLayerChange = (layerId) => {
    const newActiveLayers = { ...activeLayers, basemap: layerId };
    setActiveLayers(newActiveLayers);
    onLayerChange(newActiveLayers, opacityValues);
  };
  
  const handleOverlayChange = (layerId) => {
    const newActiveLayers = { ...activeLayers, [layerId]: !activeLayers[layerId] };
    setActiveLayers(newActiveLayers);
    onLayerChange(newActiveLayers, opacityValues);
  };
  
  const handleOpacityChange = (layerId, value) => {
    const newOpacityValues = { ...opacityValues, [layerId]: value };
    setOpacityValues(newOpacityValues);
    onLayerChange(activeLayers, newOpacityValues);
  };
  
  return (
    <div className="layer-control">
      <div className="layer-control-header">
        <div className="layer-control-title">Map Layers</div>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
      
      <div className="layer-group">
        <div className="layer-group-title">Base Maps</div>
        <div className="layer-item-control">
          <input 
            type="radio" 
            id="satellite" 
            name="basemap" 
            checked={activeLayers.basemap === 'satellite'} 
            onChange={() => handleBaseLayerChange('satellite')} 
          />
          <label htmlFor="satellite">Satellite</label>
        </div>
        <div className="layer-item-control">
          <input 
            type="radio" 
            id="hybrid" 
            name="basemap" 
            checked={activeLayers.basemap === 'hybrid'} 
            onChange={() => handleBaseLayerChange('hybrid')} 
          />
          <label htmlFor="hybrid">Hybrid</label>
        </div>
        <div className="layer-item-control">
          <input 
            type="radio" 
            id="terrain" 
            name="basemap" 
            checked={activeLayers.basemap === 'terrain'} 
            onChange={() => handleBaseLayerChange('terrain')} 
          />
          <label htmlFor="terrain">Terrain</label>
        </div>
        <div className="layer-item-control">
          <input 
            type="radio" 
            id="osm" 
            name="basemap" 
            checked={activeLayers.basemap === 'osm'} 
            onChange={() => handleBaseLayerChange('osm')} 
          />
          <label htmlFor="osm">OpenStreetMap</label>
        </div>
      </div>
      
      <div className="layer-group">
        <div className="layer-group-title">Field Data</div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="fields" 
            checked={activeLayers.fields} 
            onChange={() => handleOverlayChange('fields')} 
          />
          <label htmlFor="fields">
            <FaLayerGroup style={{ marginRight: '8px' }} />
            Field Boundaries
          </label>
        </div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="sensors" 
            checked={activeLayers.sensors} 
            onChange={() => handleOverlayChange('sensors')} 
          />
          <label htmlFor="sensors">
            <FaMapMarkerAlt style={{ marginRight: '8px' }} />
            Sensors
          </label>
        </div>
      </div>
      
      <div className="layer-group">
        <div className="layer-group-title">Analysis Layers</div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="ndvi" 
            checked={activeLayers.ndvi} 
            onChange={() => handleOverlayChange('ndvi')} 
          />
          <label htmlFor="ndvi">
            <FaChartArea style={{ marginRight: '8px' }} />
            NDVI
          </label>
          {activeLayers.ndvi && (
            <div>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={opacityValues.ndvi} 
                onChange={(e) => handleOpacityChange('ndvi', parseFloat(e.target.value))} 
                className="layer-opacity" 
              />
            </div>
          )}
        </div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="evi" 
            checked={activeLayers.evi} 
            onChange={() => handleOverlayChange('evi')} 
          />
          <label htmlFor="evi">
            <FaChartArea style={{ marginRight: '8px' }} />
            EVI
          </label>
          {activeLayers.evi && (
            <div>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={opacityValues.evi} 
                onChange={(e) => handleOpacityChange('evi', parseFloat(e.target.value))} 
                className="layer-opacity" 
              />
            </div>
          )}
        </div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="thermal" 
            checked={activeLayers.thermal} 
            onChange={() => handleOverlayChange('thermal')} 
          />
          <label htmlFor="thermal">
            <FaChartArea style={{ marginRight: '8px' }} />
            Thermal
          </label>
          {activeLayers.thermal && (
            <div>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={opacityValues.thermal} 
                onChange={(e) => handleOpacityChange('thermal', parseFloat(e.target.value))} 
                className="layer-opacity" 
              />
            </div>
          )}
        </div>
        <div className="layer-item-control">
          <input 
            type="checkbox" 
            id="ndre" 
            checked={activeLayers.ndre} 
            onChange={() => handleOverlayChange('ndre')} 
          />
          <label htmlFor="ndre">
            <FaChartArea style={{ marginRight: '8px' }} />
            NDRE
          </label>
          {activeLayers.ndre && (
            <div>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={opacityValues.ndre} 
                onChange={(e) => handleOpacityChange('ndre', parseFloat(e.target.value))} 
                className="layer-opacity" 
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapLayerControl; 